/**
 * "Kartı gözünle takip et" sunumu. Kartların etkisi ve son sırası sunucuyla ortak desteden gelir
 * (`createBonusDeck`); burada yalnız öğrencinin izleyeceği yer değiştirme koreografisi üretilir.
 * Kart kimliği = destedeki son konum. Karıştırma bitince her kart kendi kimliğiyle aynı yuvadadır,
 * böylece seçilen yuva sunucunun doğruladığı `selectedCardIndex` ile birebir aynıdır.
 */
export const BONUS_CARD_COUNT = 5;

export const bonusShuffleTiming = {
  /** Kartların açık gösterildiği süre. */
  previewMs: 2_000,
  /** Kartların kapanma (çevrilme) animasyonu. */
  flipMs: 600,
  /** Bir yer değiştirmenin süresi ve ardındaki kısa duraklama (2.–4. sınıf için takip edilebilir hız). */
  swapMs: 620,
  swapPauseMs: 140,
} as const;

export type BonusSwap = readonly [number, number];
export type BonusShufflePlan = { swaps: BonusSwap[]; startSlots: number[] };

/** 1. bonus turunda 4, sonraki her turda bir fazla yer değiştirme (en fazla 6). */
export function bonusSwapCount(round: number): number {
  return Math.min(6, Math.max(4, 3 + Math.trunc(round)));
}

/**
 * Yer değiştirme dizisi (yuva çiftleri) ve başlangıç yuvaları. `random` [0,1) üretir.
 * Arka arkaya aynı çift seçilmez; başlangıç yuvaları, dizi oynatılınca kimlik sırasına dönecek şekilde hesaplanır.
 */
export function planBonusShuffle(round: number, random: () => number): BonusShufflePlan {
  const swaps: BonusSwap[] = [];
  const count = bonusSwapCount(round);
  while (swaps.length < count) {
    const a = Math.floor(random() * BONUS_CARD_COUNT);
    let b = Math.floor(random() * (BONUS_CARD_COUNT - 1));
    if (b >= a) b += 1;
    const pair: BonusSwap = a < b ? [a, b] : [b, a];
    const previous = swaps.at(-1);
    if (previous && previous[0] === pair[0] && previous[1] === pair[1]) continue;
    swaps.push(pair);
  }
  // Son durum: kart c yuva c'de. Diziyi geriye oynatarak başlangıç yuvalarını bul.
  const cardInSlot = Array.from({ length: BONUS_CARD_COUNT }, (_, slot) => slot);
  for (let index = swaps.length - 1; index >= 0; index -= 1) {
    const [a, b] = swaps[index];
    [cardInSlot[a], cardInSlot[b]] = [cardInSlot[b], cardInSlot[a]];
  }
  const startSlots = Array.from({ length: BONUS_CARD_COUNT }, () => 0);
  cardInSlot.forEach((card, slot) => { startSlots[card] = slot; });
  return { swaps, startSlots };
}

/** `steps` kadar yer değiştirme oynatıldıktan sonra her kartın yuvası. */
export function slotsAfter(plan: BonusShufflePlan, steps: number): number[] {
  const slots = [...plan.startSlots];
  for (const [a, b] of plan.swaps.slice(0, Math.max(0, steps))) {
    const cardA = slots.indexOf(a); const cardB = slots.indexOf(b);
    slots[cardA] = b; slots[cardB] = a;
  }
  return slots;
}
